import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiClient, useAuth } from "@/lib/auth";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2, Sparkles } from "lucide-react";

export default function Billing() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(sessionId ? "polling" : "idle");
  const attempts = useRef(0);
  const { checkAuth } = useAuth();

  useEffect(() => {
    if (!sessionId) return;
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await apiClient.get(`/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setStatus("paid"); checkAuth(); return; }
        if (r.data.status === "expired") { setStatus("expired"); return; }
        if (attempts.current >= 8) { setStatus("timeout"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        if (attempts.current >= 8) setStatus("error");
        else timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, checkAuth]);

  return (
    <div className="max-w-xl mx-auto py-16 text-center" data-testid="billing-page">
      {status === "polling" && (
        <>
          <div className="mx-auto h-16 w-16 rounded-2xl bg-gradient-to-br from-primary/15 to-violet-500/15 flex items-center justify-center ring-1 ring-border">
            <Loader2 className="h-7 w-7 text-primary animate-spin" />
          </div>
          <h1 className="mt-6 text-3xl font-black font-display tracking-tighter">Vérification du paiement…</h1>
          <p className="text-muted-foreground mt-2">Cela ne prend que quelques secondes.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-500/15 flex items-center justify-center ring-1 ring-emerald-500/30">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </div>
          <h1 className="mt-6 text-4xl font-black font-display tracking-tighter">
            Bienvenue dans la <span className="text-gradient">croissance</span>
          </h1>
          <p className="text-muted-foreground mt-3">Votre nouveau plan est activé. On pousse votre commerce en avant.</p>
          <Link to="/dashboard">
            <Button className="mt-7 rounded-full h-11 px-6 btn-glow" data-testid="back-dashboard-btn">
              <Sparkles className="h-4 w-4 mr-2" />Ouvrir le tableau de bord
            </Button>
          </Link>
        </>
      )}
      {(status === "expired" || status === "timeout" || status === "error") && (
        <>
          <div className="mx-auto h-16 w-16 rounded-2xl bg-destructive/10 flex items-center justify-center ring-1 ring-destructive/30">
            <XCircle className="h-8 w-8 text-destructive" />
          </div>
          <h1 className="mt-6 text-3xl font-black font-display tracking-tighter">Paiement non finalisé</h1>
          <p className="text-muted-foreground mt-2">Si l'argent a été débité, contactez le support.</p>
          <Link to="/dashboard/pricing">
            <Button className="mt-6 rounded-full" data-testid="retry-billing-btn">Réessayer</Button>
          </Link>
        </>
      )}
      {status === "idle" && (
        <>
          <h1 className="text-3xl font-black font-display tracking-tighter">Facturation</h1>
          <p className="text-muted-foreground mt-2">Aucun paiement en cours.</p>
          <Link to="/dashboard/pricing"><Button className="mt-6 rounded-full">Voir les plans</Button></Link>
        </>
      )}
    </div>
  );
}
